import { encode } from 'iconv-lite'
import { useState, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Button, Row } from 'reactstrap'

import { getTenantUserList } from 'api/users/users'

import { showError } from 'slices/notificationSlice'
import * as Spinner from 'slices/spinnerSlice'
import { getTenantList, selectTenantsStatus } from 'slices/tenantsSlice'
import { exportData } from 'slices/utils'

import { NavMenu, Pagination, Table, CustomButton } from 'components/common'
import { ACCOUNT_TYPE_LABEL, CHARACTER_CODE_TYPES } from 'components/common/constants'
import type { TableHeaderType, TableCellType } from 'components/common/types'
import { escapeCsvField } from 'components/common/utils'

const PER_PAGE_NUMBER = 20

const header: TableHeaderType[] = [
  { value: 'テナント ID', width: '10%' },
  { value: '企業名', width: '20%', className: 'border-end' },
  { value: '事業所名', width: '30%' },
  { value: 'ご利用状況', width: '10%' },
  { value: '担当者', width: '30%' },
]

const Tenants = () => {
  const [page, setPage] = useState(1)
  const [isExporting, setIsExporting] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(getTenantList())
  }, [dispatch])

  const { partialTenants } = useSelector(selectTenantsStatus, shallowEqual)

  const tableData: TableCellType[][] = useMemo(() => {
    return partialTenants.map(tenant => [
      { value: tenant.id.toString() },
      { value: tenant.name, className: 'border-end' },
      { value: tenant.salesOfficeName || '' },
      {
        value: tenant.isActive ? '利用中' : '利用停止中',
        className: tenant.isActive ? 'text-success' : 'text-danger',
      },
      { value: tenant.personInChargeName || '' },
    ])
  }, [partialTenants])

  const tableItems: TableCellType[][] = useMemo(() => {
    return tableData.slice((page - 1) * PER_PAGE_NUMBER, page * PER_PAGE_NUMBER)
  }, [page, tableData])
  const totalPages: number = useMemo(() => {
    return Math.ceil(tableData.length / PER_PAGE_NUMBER)
  }, [tableData])

  const navigate = useNavigate()
  const onNewTenantClick = () => navigate('/tenant-create')
  const onRowClick = (index: number) => {
    const tenantId = tableItems[index][0].value
    navigate(`/tenants/${tenantId}/detail`)
  }

  const handleExportUserList = async () => {
    setIsExporting(true)
    // 複数回のAPI呼び出しの合間もSpinnerを表示し続けるため手動で制御
    dispatch(Spinner.start())

    try {
      // CognitoのTooManyRequestsExceptionを避けるため、並列実行を4つずつに制限
      const chunkSize = 4

      type TenantUserResult = {
        tenant: (typeof partialTenants)[number]
        result: Awaited<ReturnType<typeof getTenantUserList>>
      }
      const allUserResults = await Array.from({ length: Math.ceil(partialTenants.length / chunkSize) }, (_, i) =>
        partialTenants.slice(i * chunkSize, (i + 1) * chunkSize)
      ).reduce(async (accPromise, chunk) => {
        const acc = await accPromise
        const chunkResults = await Promise.all(
          chunk.map(async tenant => ({
            tenant,
            result: await getTenantUserList(tenant.id),
          }))
        )
        return [...acc, ...chunkResults]
      }, Promise.resolve<TenantUserResult[]>([]))

      const HEADER = [
        'ユーザー名',
        'メールアドレス',
        'アカウントタイプ',
        'テナントID',
        '企業名',
        '事業所名',
        '利用状況',
      ]
      const bodyRows = allUserResults.flatMap(({ tenant, result }) => {
        return result.partialUsers.flatMap(user =>
          user.partialUserHasTenants
            .filter(userHasTenant => userHasTenant.id === tenant.id)
            .map(userHasTenant => [
              userHasTenant.nickname || '',
              user.email || '',
              ACCOUNT_TYPE_LABEL[userHasTenant.role] || '不明',
              tenant.id.toString(),
              tenant.name,
              tenant.salesOfficeName || '',
              tenant.isActive && userHasTenant.isActive ? '利用中' : '利用停止中', // テナントとユーザーの両方がアクティブな場合のみ"利用中"
            ])
        )
      })

      // CSV文字列を作成
      const csvContent = [HEADER, ...bodyRows].map(row => row.map(field => escapeCsvField(field)).join(',')).join('\n')
      const csvData = encode(csvContent, CHARACTER_CODE_TYPES.SHIFT_JIS)
      const blob = new Blob([csvData], { type: 'text/csv' })
      exportData(blob, `ユーザーリスト_${new Date().toISOString().split('T')[0]}.csv`)
    } catch (error) {
      dispatch(showError())
    } finally {
      dispatch(Spinner.stop())
      setIsExporting(false)
    }
  }

  return (
    <NavMenu>
      <div className="mt-3 mx-3">
        <div className="d-flex justify-content-between">
          <div className="font-x-large fw-bold align-self-center">テナント一覧</div>
          <div className="d-flex gap-2">
            <CustomButton outline onClick={handleExportUserList} disabled={isExporting}>
              ユーザーリストエクスポート
            </CustomButton>
            <Button color="primary" className="d-flex align-items-center" onClick={onNewTenantClick}>
              <i className="icf-plus pe-2 font-large" />
              <div className="ps-1">テナント新規登録</div>
            </Button>
          </div>
        </div>
        <Row className="py-3">
          <div className="bg-white p-0">
            <Table header={header} body={tableItems} onRowClick={onRowClick} />
          </div>
          <div className="my-2 mx-auto d-flex justify-content-center">
            <Pagination totalPages={totalPages} currentPage={page} onClick={setPage} />
          </div>
        </Row>
      </div>
    </NavMenu>
  )
}

export default Tenants
